import {
  AuthRpcClient,
  createAuthRpcClient,
} from "@redotech/auth-sdk/rpc/client";
import {
  createRedoMerchantRpcClient,
  RedoMerchantRpcClient,
} from "@redotech/merchant-sdk-client/client";
import { isMockModeEnabled } from "@redotech/rpc-mock/mock-storage";
import { Store } from "@redotech/util/store";
import { epochSecondsToInstant } from "@redotech/util/temporal";
import { localStore, storageKeys } from "@redotech/web-util/storage";
import jwtDecode from "jwt-decode";
import {
  Context,
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clientVersionHeaders } from "./client-version-headers";
import { REDO_API_URL, REDO_MERCHANT_SERVER_URL } from "./config";

export interface Auth {
  expiration: Temporal.Instant;
  isAdminImpersonation: boolean;
  teamId: string;
  userId: string;
  token: string;
}

export const AuthContext = createContext<Auth | undefined>(undefined);

export type SetAuth = (auth: Auth | undefined) => void;

export const SetAuthContext = createContext<SetAuth | undefined>(undefined);

export const TOKEN_KEY = "redo.merchant_auth_token";
const MOCK_AUTH_TOKEN = "mock-token";

/** Valid ObjectId hex (matching mockTeamData/mockUserData) so code that parses auth ids with ObjectId/MongoId constructors doesn't throw. */
function createMockAuth(): Auth {
  return {
    expiration: Temporal.Now.instant().add({ hours: 24 * 365 }),
    isAdminImpersonation: false,
    teamId: "000000000000000000000002",
    userId: "000000000000000000000001",
    token: MOCK_AUTH_TOKEN,
  };
}

/**
 * Local storage for team auth token.
 */
export function teamAuthStore(teamId: string): Store<string | undefined> {
  return localStore(`${TOKEN_KEY}.${teamId}`);
}

export function teamIds(): string[] {
  return [...storageKeys(localStorage)]
    .filter((key) => key.startsWith(`${TOKEN_KEY}.`))
    .map((key) => key.replace(`${TOKEN_KEY}.`, ""));
}

export function parseAuth(token: string | undefined): Auth | undefined {
  if (!token || token === MOCK_AUTH_TOKEN) {
    return undefined;
  }
  try {
    const decoded = jwtDecode<any>(token);
    const teamId = decoded.team_id ?? decoded.aud.split("/")[1];
    const userId = decoded.id ?? decoded.sub;
    if (
      !teamId ||
      typeof teamId !== "string" ||
      !userId ||
      typeof userId !== "string"
    ) {
      throw new TypeError("Not a valid Auth object");
      // Do not print sensitive information - only uncomment this to test locally
      // throw new TypeError(`Not a valid Auth object: ${JSON.stringify(decoded)}`);
    }
    return {
      expiration: epochSecondsToInstant(decoded.exp),
      isAdminImpersonation: decoded.src === "admin_login",
      teamId,
      token,
      userId,
    };
  } catch (error) {
    console.error("Failed to parse token", error);
    // Do not print sensitive information - only uncomment this to test locally
    // console.error(`Failed to parse invalid token ${token}`, error);
    return undefined;
  }
}

/** @returns whether the auth is expired (or expires within the next second) */
function isExpired(expiration: Temporal.Instant): boolean {
  const duration = expiration.since(Temporal.Now.instant());
  // Give ourselves 1 second leeway so it doesn't expire while trying to load the page
  return (
    Temporal.Duration.compare(
      Temporal.Duration.from({ seconds: 1 }),
      duration,
    ) > 0
  );
}

export const RedoMerchantUnauthenticatedAuthRpcClientContext: Context<
  AuthRpcClient | undefined
> = createContext<AuthRpcClient | undefined>(undefined);
RedoMerchantUnauthenticatedAuthRpcClientContext.displayName =
  "RedoMerchantAuthRpcClientContext";

// Session-less merchant RPC client for the public signup endpoints.
export const RedoMerchantAnonymousRpcClientContext: Context<
  RedoMerchantRpcClient | undefined
> = createContext<RedoMerchantRpcClient | undefined>(undefined);
RedoMerchantAnonymousRpcClientContext.displayName =
  "RedoMerchantAnonymousRpcClientContext";

export const RedoMerchantAuthRpcClientContext: Context<
  AuthRpcClient | undefined
> = createContext<AuthRpcClient | undefined>(undefined);
RedoMerchantAuthRpcClientContext.displayName =
  "RedoMerchantAuthRpcClientContext";

export const AuthRequired = memo(function AuthRequired({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useLayoutEffect(() => {
    if (!auth) {
      const next = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  return auth ? <>{children}</> : null;
});

function teamIdFromPath(pathname: string): string | undefined {
  const parts = pathname.split("/");
  return parts[1] === "stores" ? parts[2] : undefined;
}

function authStoreFor(teamId: string | undefined): Store<string | undefined> {
  return teamId ? teamAuthStore(teamId) : localStore(TOKEN_KEY); // TODO(paul): Fallback unnecessary now?
}

let soleStoreSignInRoots: ReadonlySet<string> = new Set();

/** Unscoped top-level paths, opened from links outside the app, where a browser without a live plain sign-in uses its only live per-store one. */
export function registerSoleStoreSignInRoots(roots: readonly string[]): void {
  soleStoreSignInRoots = new Set(roots);
}

function soleLiveTeamAuth(): Auth | undefined {
  const live = teamIds().flatMap((teamId) => {
    const parsed = parseAuth(teamAuthStore(teamId).get());
    return parsed && !isExpired(parsed.expiration) ? [parsed] : [];
  });
  return live.length === 1 ? live[0] : undefined;
}

/**
 * The auth `AuthProvider` starts with for a first load at `pathname`. Also
 * deletes per-team tokens that are expired, invalid, or stored under another
 * team's key. A scoped path whose own team key is missing or expired falls
 * back to the plain key when that holds a live token for the same team, which
 * `AuthProvider` then writes back to the team key.
 */
export function readStoredAuth(pathname: string): Auth | undefined {
  // Delete expired, invalid, or misfiled tokens
  for (const key of storageKeys(localStorage)) {
    if (!key.startsWith(`${TOKEN_KEY}.`)) {
      continue;
    }
    const store = localStore(key);
    const parsed = parseAuth(store.get());
    if (
      parsed &&
      !isExpired(parsed.expiration) &&
      key === `${TOKEN_KEY}.${parsed.teamId}`
    ) {
      continue;
    }
    store.set(undefined);
  }
  const teamId = teamIdFromPath(pathname);
  const stored = authStoreFor(teamId).get();
  const parsed = stored ? parseAuth(stored) : undefined;
  if (!teamId) {
    if (parsed && !isExpired(parsed.expiration)) {
      return parsed;
    }
    if (stored) {
      localStore(TOKEN_KEY).set(undefined);
    }
    const sole = soleStoreSignInRoots.has(pathname.split("/")[1] ?? "")
      ? soleLiveTeamAuth()
      : undefined;
    if (sole) {
      return sole;
    }
  } else {
    if (parsed && !isExpired(parsed.expiration)) {
      return parsed;
    }
    const plainToken = localStore(TOKEN_KEY).get();
    const plain = plainToken ? parseAuth(plainToken) : undefined;
    if (plain?.teamId === teamId && !isExpired(plain.expiration)) {
      return plain;
    }
  }
  if (isMockModeEnabled()) {
    return createMockAuth();
  }
  return;
}

let sessionAuth: Auth | undefined;

/** Publishes and persists the auth before returning, so a full navigation right after `setAuth` keeps it. Only `AuthProvider` calls this. */
function commitSessionAuth(auth: Auth | undefined): void {
  sessionAuth = auth;
  if (auth) {
    if (auth.token !== MOCK_AUTH_TOKEN) {
      authStoreFor(auth.teamId).set(auth.token);
    }
  } else {
    authStoreFor(teamIdFromPath(location.pathname)).set(undefined);
  }
}

/** The auth the app is running with now, the value `AuthContext` holds, for code outside React such as route loaders. */
export function getSessionAuth(): Auth | undefined {
  return sessionAuth;
}

export const AuthProvider = memo(function AuthProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [auth, setAuthState] = useState<Auth | undefined>(() => {
    const initial = readStoredAuth(location.pathname);
    commitSessionAuth(initial);
    return initial;
  });
  const setAuth = useCallback<SetAuth>((next) => {
    commitSessionAuth(next);
    setAuthState(next);
  }, []);

  const redoMerchantUnauthenticatedAuthClient = createAuthRpcClient({
    baseURL: new URL(`${REDO_API_URL}/auth-rpc`),
  });

  const redoMerchantAnonymousRpcClient = useMemo(
    () =>
      createRedoMerchantRpcClient({
        baseURL: new URL(`${REDO_MERCHANT_SERVER_URL}/rpc`),
        headers: { ...clientVersionHeaders },
      }),
    [],
  );

  const redoMerchantAuthClient = useMemo(() => {
    if (!auth) {
      return undefined;
    }
    return createAuthRpcClient({
      baseURL: new URL(`${REDO_API_URL}/auth-rpc`),
      headers: { Authorization: `Bearer ${auth.token}` },
    });
  }, [auth]);

  useEffect(() => {
    if (!auth) {
      return;
    }
    // Delete the auth when it expires
    const duration = auth.expiration.since(Temporal.Now.instant());
    const timeout = setTimeout(
      () => setAuth(undefined),
      Math.min(2_147_483_647, duration.total("milliseconds")),
    );
    return () => clearTimeout(timeout);
  }, [auth, setAuth]);

  return (
    <AuthContext.Provider value={auth}>
      <SetAuthContext.Provider value={setAuth}>
        <RedoMerchantUnauthenticatedAuthRpcClientContext.Provider
          value={redoMerchantUnauthenticatedAuthClient}
        >
          <RedoMerchantAnonymousRpcClientContext.Provider
            value={redoMerchantAnonymousRpcClient}
          >
            <RedoMerchantAuthRpcClientContext.Provider
              value={redoMerchantAuthClient}
            >
              {children}
            </RedoMerchantAuthRpcClientContext.Provider>
          </RedoMerchantAnonymousRpcClientContext.Provider>
        </RedoMerchantUnauthenticatedAuthRpcClientContext.Provider>
      </SetAuthContext.Provider>
    </AuthContext.Provider>
  );
});
